/**
 * The office socket.
 *
 * Same handshake as chat's client (`weco-jwt.v1` subprotocol carrying the
 * access token) against a different service, so the two connections are
 * independent: a chat outage must not empty the floor.
 */

export type ConnectionStatus = "connecting" | "connected" | "offline";

export interface ServerFrame {
  type: string;
  payload: Record<string, unknown>;
}

type Listener = (frame: ServerFrame) => void;
type StatusListener = (status: ConnectionStatus) => void;

const SUBPROTOCOL = "weco-jwt.v1";
const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
const JITTER = 0.25;
/** Matches the server's OFFICE_HEARTBEAT_INTERVAL_SECONDS default. */
const HEARTBEAT_MS = 30_000;
const MAX_AUTH_RETRIES = 1;

export interface OfficeRealtimeClientOptions {
  url: string;
  getToken: () => Promise<string | null>;
  onAuthFailure?: () => void;
}

export class OfficeRealtimeClient {
  private ws: WebSocket | null = null;
  private status: ConnectionStatus = "offline";
  private backoffMs = BACKOFF_MIN_MS;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private closedByUs = false;
  private authRetries = 0;
  /** Guards the await gap in connect(); see the comment there. */
  private connecting = false;

  constructor(private opts: OfficeRealtimeClientOptions) {}

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatusChange(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  getStatus(): ConnectionStatus {
    return this.status;
  }

  async connect(): Promise<void> {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    // Fetching the token is async, and `this.ws` is still null across that
    // await — so the readyState guard above cannot see a connect that is
    // already in flight. Without this flag, two overlapping calls each create
    // a socket and the first is orphaned while still open: one leaked
    // connection per remount, which React's development double-mount produces
    // on every single page load.
    if (this.connecting) return;
    this.connecting = true;
    this.closedByUs = false;
    this.setStatus("connecting");

    let token: string | null;
    try {
      token = await this.opts.getToken();
    } finally {
      this.connecting = false;
    }
    if (!token) {
      this.setStatus("offline");
      this.opts.onAuthFailure?.();
      return;
    }
    // Another call may have won the race while we were awaiting.
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    let socket: WebSocket;
    try {
      socket = new WebSocket(this.opts.url, [SUBPROTOCOL, token]);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = socket;

    socket.onopen = () => {
      if (this.ws !== socket) return;
      this.setStatus("connected");
      this.backoffMs = BACKOFF_MIN_MS;
      this.authRetries = 0;
      this.startHeartbeat();
    };

    socket.onmessage = (event) => {
      if (this.ws !== socket) return;
      let frame: ServerFrame;
      try {
        frame = JSON.parse(event.data as string) as ServerFrame;
      } catch {
        return;
      }
      for (const listener of this.listeners) listener(frame);
    };

    socket.onclose = (event) => {
      // A superseded socket closing says nothing about the live one.
      if (this.ws !== socket) return;
      this.stopHeartbeat();
      this.ws = null;
      if (this.closedByUs) {
        this.setStatus("offline");
        return;
      }
      // 4401 is "reconnect with a fresh token" as often as it is a revoked
      // session, so try once through connect() (which re-fetches the token)
      // before giving up.
      if (event.code === 4401 && this.authRetries < MAX_AUTH_RETRIES) {
        this.authRetries += 1;
        void this.connect();
        return;
      }
      if (event.code === 4401 || event.code === 4403) {
        this.setStatus("offline");
        this.opts.onAuthFailure?.();
        return;
      }
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      /* onclose always follows; reconnection is handled there. */
    };
  }

  disconnect(): void {
    this.closedByUs = true;
    this.stopHeartbeat();
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
    this.setStatus("offline");
  }

  send(message: Record<string, unknown>): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(message));
  }

  move(x: number, y: number, facing: string): void {
    this.send({ type: "move", x, y, facing });
  }

  setStatusRemote(status: string, statusMessage?: string): void {
    this.send({ type: "presence.set", status, status_message: statusMessage ?? "" });
  }

  reportIdle(): void {
    this.send({ type: "idle" });
  }

  /**
   * Take the lift over the socket rather than over REST.
   *
   * The server has to move this connection between floor channel groups and
   * send a fresh snapshot; doing it through the HTTP endpoint would change the
   * user's floor server-side while leaving this socket subscribed to the floor
   * they just left, still holding its map. The REST endpoint stays for clients
   * without a live socket.
   */
  switchFloor(mapId: string): void {
    this.send({ type: "floor.switch", map_id: mapId });
  }

  /** Report joining or leaving a call, so presence can outrank the zone. */
  setOnCall(onCall: boolean): void {
    this.send({ type: "call.state", on_call: onCall });
  }

  /** Put a thumb on the announcement circle's button. */
  takeChannel(): void {
    this.send({ type: "announce.take" });
  }

  releaseChannel(): void {
    this.send({ type: "announce.release" });
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const listener of this.statusListeners) listener(status);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      this.send({ type: "heartbeat" });
    }, HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    this.setStatus("offline");
    if (this.reconnectTimer !== null) return;
    const jitter = this.backoffMs * JITTER * (Math.random() * 2 - 1);
    const delay = Math.max(BACKOFF_MIN_MS, this.backoffMs + jitter);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
    this.backoffMs = Math.min(this.backoffMs * 2, BACKOFF_MAX_MS);
  }
}
